import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useContext, useEffect, useRef, useState } from 'react';
import Download from 'lucide-react/dist/esm/icons/download.mjs';
import Eye from 'lucide-react/dist/esm/icons/eye.mjs';
import FileText from 'lucide-react/dist/esm/icons/file-text.mjs';
import LoaderCircle from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import X from 'lucide-react/dist/esm/icons/x.mjs';
import { AutoSaveContext } from './autoSaveContext.js';
import { artifactFileName, fileSizeLabel, previewMediaType, verifiedPreviewMedia } from './fileMedia.js';
/**
 * Remote files authorize a fresh fetch; local files must exist in this conversation's store.
 *
 * "Create my PPP" (ppp.ts): `openPdf` is a counter a chip outside the cards raises to open the first PDF's preview, and
 * `pdfFallback` supplies the report's durable PDF when the 24-hour file answers 404 (it is then saved, not previewed).
 * `webPages`: a web page (.html, .htm, or a .txt that reads as one) says so under its name and has a Preview button that
 * opens it in the side panel's sealed frame (owner, 2026-10-22); it never renders in this card or its dialog.
 *
 * In the desktop app every file here is also copied to Documents\Gyro\<chat name> as soon as it appears (owner, 2026-10-22;
 * desktopAutoSave.tsx): one line under the list says where, with Open folder, or why not (the full list only; the task
 * panel's compact copy shows none).
 */
export function GeneratedFiles({ artifacts, client, resolveLocal, compact = false, openPdf = 0, pdfFallback, webPages }) {
    const [pending, setPending] = useState(null);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null);
    const dialog = useRef(null);
    const request = useRef(null);
    const live = useRef(true);
    const urls = useRef(new Map());
    const autoSave = useContext(AutoSaveContext);
    useEffect(() => {
        live.current = true;
        return () => {
            live.current = false;
            request.current?.abort();
            for (const [url, timer] of urls.current) {
                clearTimeout(timer);
                URL.revokeObjectURL(url);
            }
            urls.current.clear();
        };
    }, []);
    useEffect(() => {
        if (!preview)
            return;
        const element = dialog.current;
        element?.showModal();
        return () => { element?.close(); URL.revokeObjectURL(preview.url); };
    }, [preview]);
    const save = (blob, name) => {
        // The download URL always has an inert MIME, including HTML/SVG artifacts.
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = artifactFileName(name);
        link.rel = 'noopener';
        document.body.append(link);
        link.click();
        link.remove();
        const timer = setTimeout(() => { URL.revokeObjectURL(url); urls.current.delete(url); }, 60_000);
        urls.current.set(url, timer);
    };
    const open = async (artifact, action) => {
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        setPending(`${artifact.taskId}:${artifact.id}`);
        setError('');
        try {
            let blob;
            try {
                blob = artifact.source === 'desktop'
                    ? resolveLocal ? resolveLocal(artifact) : (() => { throw new Error('This local file is no longer available. Use Export to create it again.'); })()
                    : await client.downloadArtifact(artifact, { signal: controller.signal });
            }
            catch (cause) {
                // The 24-hour file is gone (404): a PPP report's PDF is still kept, so that is saved instead when the fallback
                // finds this very file's report; when it finds none, the 404's own sentence stays.
                if (!pdfFallback || artifact.mediaType !== 'application/pdf' || cause?.status !== 404)
                    throw cause;
                const durable = await pdfFallback(artifact, { signal: controller.signal });
                if (!durable)
                    throw cause;
                if (live.current && !controller.signal.aborted)
                    save(durable.blob, durable.name);
                return;
            }
            if (!live.current || controller.signal.aborted)
                return;
            if (action === 'preview') {
                const type = await verifiedPreviewMedia(blob, artifact.mediaType);
                if (!live.current || controller.signal.aborted)
                    return;
                if (!type)
                    throw new Error('Preview is unavailable for this file. You can download it instead.');
                setPreview({ artifact, url: URL.createObjectURL(new Blob([blob], { type })), mediaType: type });
            }
            else {
                save(blob, artifact.name);
            }
        }
        catch (cause) {
            if (live.current && !controller.signal.aborted)
                setError(cause instanceof Error ? cause.message : 'This file could not be opened. Try again.');
        }
        finally {
            if (live.current && !controller.signal.aborted)
                setPending(null);
        }
    };
    // The Open PDF chip: each new count opens the first PDF's preview once.
    const openedPdf = useRef(openPdf);
    useEffect(() => {
        if (openPdf === openedPdf.current)
            return;
        openedPdf.current = openPdf;
        const pdf = artifacts.find((artifact) => artifact.mediaType === 'application/pdf');
        if (pdf)
            void open(pdf, 'preview');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPdf]);
    if (!artifacts.length)
        return null;
    return _jsxs("div", { className: `gyro-generated-files${compact ? ' compact' : ''}`, children: [!compact && _jsx("div", { className: "gyro-generated-title", children: "Generated files" }), artifacts.map((artifact) => {
                const webPage = webPages?.isWebPage(artifact) === true;
                return _jsxs("div", { className: "gyro-generated-file", children: [_jsx(FileText, { size: 19, "aria-hidden": "true" }), _jsxs("div", { className: "gyro-generated-copy", children: [_jsx("strong", { title: artifact.name, children: artifact.name }), _jsxs("small", { children: [fileSizeLabel(artifact.sizeBytes), webPage ? ' · web page' : ''] })] }), _jsxs("div", { className: "gyro-generated-actions", children: [pending === `${artifact.taskId}:${artifact.id}` && _jsx(LoaderCircle, { className: "gyro-file-spinner", size: 15, "aria-label": "Loading file" }), webPage && _jsx("button", { type: "button", className: "gyro-preview-button", "aria-label": `Preview ${artifact.name} in the side panel`, title: "Preview", onClick: () => webPages.preview(artifact), children: "Preview" }), previewMediaType(artifact.mediaType) && _jsx("button", { type: "button", className: "gyro-file-button", "aria-label": `Preview ${artifact.name}`, title: "Preview", disabled: pending !== null, onClick: () => void open(artifact, 'preview'), children: _jsx(Eye, { size: 15 }) }), _jsx("button", { type: "button", className: "gyro-file-button", "aria-label": `Download ${artifact.name}`, title: "Download", disabled: pending !== null, onClick: () => void open(artifact, 'download'), children: _jsx(Download, { size: 15 }) })] })] }, `${artifact.taskId}:${artifact.id}`);
            }), autoSave && !compact && _jsx(autoSave.Note, { artifacts: artifacts }), error && !preview && _jsx("p", { className: "gyro-file-error", role: "alert", children: error }), preview && _jsxs("dialog", { ref: dialog, className: "gyro-file-dialog", "aria-label": `Preview ${preview.artifact.name}`, onCancel: (event) => { event.preventDefault(); setPreview(null); }, onKeyDown: (event) => { if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    setPreview(null);
                } }, onClick: (event) => { if (event.target === event.currentTarget)
                    setPreview(null); }, children: [_jsxs("div", { className: "gyro-file-dialog-head", children: [_jsx(FileText, { size: 20, "aria-hidden": "true" }), _jsx("strong", { children: preview.artifact.name }), _jsx("button", { type: "button", className: "gyro-file-button", disabled: pending !== null, onClick: () => void open(preview.artifact, 'download'), "aria-label": `Download ${preview.artifact.name}`, title: "Download", children: _jsx(Download, { size: 18 }) }), _jsx("button", { type: "button", className: "gyro-file-button", onClick: () => setPreview(null), "aria-label": "Close file preview", autoFocus: true, children: _jsx(X, { size: 20 }) })] }), _jsx("div", { className: "gyro-file-viewer", children: preview.mediaType === 'application/pdf' ? _jsx("iframe", { title: preview.artifact.name, src: preview.url, referrerPolicy: "no-referrer" }) : _jsx("img", { src: preview.url, alt: preview.artifact.name }) }), _jsx("p", { className: "gyro-file-preview-note", children: preview.mediaType === 'application/pdf' ? 'If your app cannot show the PDF, use Download to open it in your PDF viewer.' : 'Preview of the generated image.' }), error && _jsx("p", { className: "gyro-file-error", role: "alert", children: error })] })] });
}
//# sourceMappingURL=GeneratedFiles.js.map